/**
 * ONE teardown — the registry of every account-scoped store reclaimed on
 * sign-out, and the exemptions for the ones deliberately left alone.
 *
 * ## Why a registry rather than a longer `clearAuthAction`
 *
 * `clearAuthAction` used to reset two things: the auth blob and
 * `launchedWorkspaceIdsAtom`. Everything else survived — the org list, the whole
 * react-query cache, the bridge tenant context, the `desktop-auth-handoff`
 * stash. Nothing was wrong with the code that cleared those two; the problem is
 * that the set of things needing clearing GROWS with every feature, and an
 * inline list has no way to notice when it falls behind. The next atom is always
 * added by someone who is not thinking about sign-out.
 *
 * So this file borrows the shape that already solved the identical problem
 * server-side: `go/internal/account/architecture_test.go`'s
 * `TestEveryAccountScopedTableIsReclaimedOrExempt` enumerates the LIVE schema
 * and fails naming any account-scoped table that is neither reclaimed nor
 * exempted. Here the "live schema" is the set of atoms exported from
 * `src/stores/**`, enumerated from source by `teardown.test.ts`. Adding an atom
 * without classifying it turns that test red.
 *
 * An exemption is a reviewed decision, so it carries a written reason. An
 * exemption with no reason is indistinguishable from an oversight.
 */

import type { Getter, Setter } from 'jotai';
import { ALL_FILTER, DESKTOP_HANDOFF_KEY } from '@localagent/sdk';
import { queryClient } from '../../providers/QueryProvider';
import { launchedWorkspaceIdsAtom } from '../workspace/atoms';
import { workspaceProjectionsAtom } from '../workspace/projection';
import { chatDraftAtomFamily, clearChatDraftsAction, openQuestionsErrorAtom } from '../chat/atoms';
import { chatSurfaceEventsAtom } from '../chat/session-events';
import { errorEntriesAtom } from '../errors/atoms';
import { notificationEntriesAtom, notificationMutePrefsAtom } from '../notifications/atoms';
import { selectedPositionIdAtom } from '../orgchart/atoms';
import { activeTeamIdAtom, setWorkItemSessionsAction, workItemSessionsAtom } from '../work/atoms';
import {
  sessionGoalFilterAtom,
  sessionRoleFilterAtom,
  sessionStatusFilterAtom,
  sessionWorkspaceFilterAtom,
} from '../chat/sidebar';
import {
  panelChatSessionIdsAtom,
  shogunComposerDraftAtom,
  shogunViewSnapshotAtom,
} from '../shogun/atoms';
import { liveFeedHandleAtom, liveFeedUnseenCountAtom } from '../sdk/live-feed-atom';

/**
 * The `localStorage` key `/desktop-auth` stashes `{returnUrl, nonce}` under
 * while a desktop browser-bounce handoff is in flight.
 *
 * Duplicated as a constant here rather than imported from `routes/desktop-auth`
 * so a store module never imports a route component (that direction is a cycle
 * waiting to happen); `desktop-auth.stash.test.ts` asserts the two strings are
 * the same, so the duplication cannot drift silently.
 */
// The stash key lives in the SDK now (sdk ADR-011) —
// but the SDK writes it under ITS namespace via ITS storage adapter, so the
// shell's reclaim entry delegates rather than re-declaring the key.

/**
 * One reclaimed store.
 *
 * `dirty` / `isClean` exist so the behavioural test can prove `reset` actually
 * works: it dirties every registered store, runs the real `clearAuthAction`, and
 * asserts every one reads clean again. Without them "registered" would be
 * self-certifying — a registry entry whose `reset` does nothing would still pass
 * a test that only checks the entry is present.
 */
export interface AccountScopedStore {
  /**
   * The atom's exported identifier (e.g. `errorEntriesAtom`), or a stable slug
   * for a store that is not an atom (`react-query-cache`).
   */
  id: string;
  /** True when this entry names something other than a `src/stores/**` atom. */
  external?: boolean;
  /** Set to the reason when the identity-change sweep must SKIP this entry; sign-out still resets it. */
  identityChangeSkipReason?: string;
  /** Put the store into a non-default, account-scoped state. */
  dirty: (get: Getter, set: Setter) => void;
  /** True when the store holds no account-scoped residue. */
  isClean: (get: Getter) => boolean;
  /** Reclaim it. May be async when reclaiming crosses a process boundary. */
  reset: (get: Getter, set: Setter) => void | Promise<void>;
}

/**
 * One store this registry does NOT reclaim with an entry of its own, and the
 * reason. Three reasons qualify, and each must be written out:
 *
 *   1. it is derived and has no stored value of its own;
 *   2. it is not account-scoped (device/app configuration, a clock);
 *   3. another entry already reclaims it — name that entry.
 *
 * "I did not get to it" is not one of them.
 */
export interface AccountScopedExemption {
  id: string;
  reason: string;
}

/**
 * A concrete `chatDraftAtomFamily` member, used only to prove the family's
 * backing map is emptied. The id is deliberately not a real session id.
 */
const chatDraftProbe = chatDraftAtomFamily('teardown-probe-session');

export const ACCOUNT_SCOPED_TEARDOWN: AccountScopedStore[] = [
  {
    id: 'openQuestionsErrorAtom',
    dirty: (_get, set) => set(openQuestionsErrorAtom, new Error('stale open-questions failure')),
    isClean: (get) => get(openQuestionsErrorAtom) === null,
    reset: (_get, set) => set(openQuestionsErrorAtom, null),
  },
  {
    id: 'launchedWorkspaceIdsAtom',
    dirty: (_get, set) => set(launchedWorkspaceIdsAtom, ['ws-stale']),
    isClean: (get) => get(launchedWorkspaceIdsAtom).length === 0,
    reset: (_get, set) => set(launchedWorkspaceIdsAtom, []),
  },
  {
    id: 'workspaceProjectionsAtom',
    dirty: (_get, set) =>
      set(workspaceProjectionsAtom, {
        'ws-stale': {
          projection: { workspace_id: 'ws-stale', revision: 1 } as never,
          refreshRequested: false,
          revisionGap: null,
          refreshIndexRevision: null,
        },
      }),
    isClean: (get) => Object.keys(get(workspaceProjectionsAtom)).length === 0,
    reset: (_get, set) => set(workspaceProjectionsAtom, {}),
  },
  {
    id: 'chatDraftAtomFamily',
    // The family's backing map is module-private; `clearChatDraftsAction` is the
    // one handle onto it. Dirty/clean go through a concrete family member.
    dirty: (_get, set) => set(chatDraftProbe, 'unsent prompt from the previous account'),
    isClean: (get) => get(chatDraftProbe) === '',
    reset: (_get, set) => set(clearChatDraftsAction),
  },
  {
    id: 'errorEntriesAtom',
    dirty: (_get, set) =>
      set(errorEntriesAtom, [{ id: 'err-stale', read: false, severity: 'error' } as never]),
    isClean: (get) => get(errorEntriesAtom).length === 0,
    reset: (_get, set) => set(errorEntriesAtom, []),
  },
  {
    id: 'notificationEntriesAtom',
    dirty: (_get, set) =>
      set(notificationEntriesAtom, [{ id: 'notif-stale', read: false } as never]),
    isClean: (get) => get(notificationEntriesAtom).length === 0,
    reset: (_get, set) => set(notificationEntriesAtom, []),
  },
  {
    id: 'notificationMutePrefsAtom',
    dirty: (_get, set) =>
      set(notificationMutePrefsAtom, { mutedKinds: ['info' as never], mutedOrigins: ['api'] }),
    isClean: (get) => {
      const prefs = get(notificationMutePrefsAtom);
      return prefs.mutedKinds.length === 0 && prefs.mutedOrigins.length === 0;
    },
    reset: (_get, set) => set(notificationMutePrefsAtom, { mutedKinds: [], mutedOrigins: [] }),
  },
  {
    id: 'liveFeedHandleAtom',
    dirty: (_get, set) => set(liveFeedHandleAtom, {} as never),
    isClean: (get) => get(liveFeedHandleAtom) === null,
    reset: (_get, set) => set(liveFeedHandleAtom, null),
  },
  {
    id: 'liveFeedUnseenCountAtom',
    dirty: (_get, set) => set(liveFeedUnseenCountAtom, 3),
    isClean: (get) => get(liveFeedUnseenCountAtom) === 0,
    reset: (_get, set) => set(liveFeedUnseenCountAtom, 0),
  },
  {
    id: 'selectedPositionIdAtom',
    dirty: (_get, set) => set(selectedPositionIdAtom, 'pos-stale'),
    isClean: (get) => get(selectedPositionIdAtom) === null,
    reset: (_get, set) => set(selectedPositionIdAtom, null),
  },
  {
    id: 'activeTeamIdAtom',
    dirty: (_get, set) => set(activeTeamIdAtom, 'team-stale'),
    isClean: (get) => get(activeTeamIdAtom) === null,
    reset: (_get, set) => set(activeTeamIdAtom, null),
  },
  {
    id: 'workItemSessionsAtom',
    // Work-item → session ids from the previous account. The base atom is
    // module-private, so dirty/clean/reset all go through its writer action.
    dirty: (_get, set) =>
      set(setWorkItemSessionsAction, { 'ticket-stale': { sessionId: 'sess-stale', running: true } }),
    isClean: (get) => Object.keys(get(workItemSessionsAtom)).length === 0,
    reset: (_get, set) => set(setWorkItemSessionsAction, {}),
  },
  {
    id: 'sessionGoalFilterAtom',
    // A goal id from the previous account, persisted in localStorage. Left in
    // place it filters the next account's session list to an id it cannot own,
    // which reads as an empty list rather than as a stale filter.
    dirty: (_get, set) => set(sessionGoalFilterAtom, 'goal-stale'),
    isClean: (get) => get(sessionGoalFilterAtom) === ALL_FILTER,
    reset: (_get, set) => set(sessionGoalFilterAtom, ALL_FILTER),
  },
  {
    id: 'sessionRoleFilterAtom',
    // A persona name from the previous account's org chart; same failure shape
    // as the goal filter above.
    dirty: (_get, set) => set(sessionRoleFilterAtom, 'reviewer-stale'),
    isClean: (get) => get(sessionRoleFilterAtom) === ALL_FILTER,
    reset: (_get, set) => set(sessionRoleFilterAtom, ALL_FILTER),
  },
  {
    id: 'sessionStatusFilterAtom',
    dirty: (_get, set) => set(sessionStatusFilterAtom, 'active'),
    isClean: (get) => get(sessionStatusFilterAtom) === 'all',
    reset: (_get, set) => set(sessionStatusFilterAtom, 'all'),
  },
  {
    id: 'sessionWorkspaceFilterAtom',
    dirty: (_get, set) => set(sessionWorkspaceFilterAtom, 'workspace-stale'),
    isClean: (get) => get(sessionWorkspaceFilterAtom) === ALL_FILTER,
    reset: (_get, set) => set(sessionWorkspaceFilterAtom, ALL_FILTER),
  },
  {
    id: 'shogunViewSnapshotAtom',
    // Record ids and labels from the previous account's boards; the next
    // account's first docked turn would otherwise be told about them.
    dirty: (_get, set) =>
      set(shogunViewSnapshotAtom, {
        route: '/work',
        context: { visible: [{ type: 'ticket', id: 'tk-stale' }] },
      }),
    isClean: (get) => get(shogunViewSnapshotAtom) === null,
    reset: (_get, set) => set(shogunViewSnapshotAtom, null),
  },
  {
    id: 'panelChatSessionIdsAtom',
    // Session ids from the previous account, persisted in localStorage. The
    // next account's first docked turn would try to resume a thread it cannot
    // read.
    dirty: (_get, set) => set(panelChatSessionIdsAtom, { default: 'sess-stale' }),
    isClean: (get) => Object.keys(get(panelChatSessionIdsAtom)).length === 0,
    reset: (_get, set) => set(panelChatSessionIdsAtom, {}),
  },
  {
    id: 'shogunComposerDraftAtom',
    dirty: (_get, set) => set(shogunComposerDraftAtom, 'stale draft'),
    isClean: (get) => get(shogunComposerDraftAtom) === null,
    reset: (_get, set) => set(shogunComposerDraftAtom, null),
  },
  {
    id: 'chatSurfaceEventsAtom',
    // The previous account's live session event log; a consumer view (the
    // search results panel) would otherwise keep projecting its tool results.
    dirty: (_get, set) =>
      set(chatSurfaceEventsAtom, { events: [], getEventData: async () => ({}) }),
    isClean: (get) => get(chatSurfaceEventsAtom) === null,
    reset: (_get, set) => set(chatSurfaceEventsAtom, null),
  },
  {
    id: 'react-query-cache',
    external: true,
    // Every hook in the shell reads through this cache, and every entry in it is
    // one account's data. Leaving it populated is how the next account briefly
    // saw the previous account's sessions/personas/workspaces.
    dirty: () => queryClient.setQueryData(['sessions', 'ws-stale'], [{ id: 'sess-stale' }]),
    isClean: () => queryClient.getQueryCache().getAll().length === 0,
    reset: () => queryClient.clear(),
  },
  {
    id: 'desktop-auth-handoff',
    external: true,
    identityChangeSkipReason:
      'in-flight protocol state, not account data. Signing the desktop app in as B from a browser ' +
      'signed in as A IS an identity change, and an open app tab applies it (SessionActivationGate ' +
      'acquires the origin-wide Stytch session) before `/desktop-auth` entry (b) claims the stash — ' +
      'sweeping here would dead-end that handoff on "missing handoff state". Sign-out still clears it.',
    // A stale `{returnUrl, nonce}` stash left behind by an abandoned desktop
    // handoff is claimed by the NEXT visit to `/desktop-auth`, which then hands
    // a fresh session to a return URL the current user never asked for.
    dirty: () =>
      globalThis.localStorage?.setItem(
        DESKTOP_HANDOFF_KEY,
        JSON.stringify({ returnUrl: 'http://127.0.0.1:1/cb', nonce: 'x'.repeat(32) })
      ),
    isClean: () =>
      globalThis.localStorage?.getItem(DESKTOP_HANDOFF_KEY) === null,
    reset: () => globalThis.localStorage?.removeItem(DESKTOP_HANDOFF_KEY),
  },
];

export const ACCOUNT_SCOPED_EXEMPTIONS: AccountScopedExemption[] = [
  // ── the holder phase itself ────────────────────────────────────────────────
  {
    id: 'authPhaseAtom',
    reason:
      'the holder phase IS what sign-out clears — `clearAuthAction` applies a LocalClear event ' +
      'through the SDK reducer (the only legal local route to no_credential). It is the subject ' +
      'of the teardown, not a bystander store the registry resets behind the reducer’s back.',
  },
  // ── derived projections: they cannot hold residue their source does not ────
  ...(
    [
      'authPhaseNameAtom',
      'authDegradedReasonAtom',
      'holdsIdentityAtom',
      'authIdentityAtom',
      'authMaterialAtom',
      'authExpiresAtAtom',
      'tokenAtom',
      'claimsAtom',
      'stytchSessionTokenAtom',
      'stytchSessionJwtAtom',
      'visibleErrorEntriesAtom',
      'unreadErrorCountAtom',
      'apiBaseUrlAtom',
      'sdkConfiguredAtom',
      'workspaceProjectionAtomFamily',
    ] as const
  ).map((id) => ({
    id,
    reason:
      'read-only derived atom — it has no stored value of its own and reads clean the instant ' +
      'its source does. Resetting it is not expressible, and would be redundant if it were.',
  })),
  // ── not account-scoped ────────────────────────────────────────────────────
  {
    id: 'sdkAtom',
    reason:
      'the installed SDK reads its bearer live through the AuthProvider, which `clearAuthAction` ' +
      "invalidates. The instance holds no account data — the account-scoped SDK state it DOES " +
      'hold is reclaimed by the registry external sdk-storage entry, which calls the SDK reset.',
  },
  {
    id: 'bridgeEventStreamAtom',
    reason:
      'the live per-tab BridgeEventStream is OWNED by useWebBridgeClient, whose effect is keyed ' +
      'on isAuthenticated/hasToken and therefore closes the socket and nulls this slot the moment ' +
      'the phase leaves `usable`. Nulling it from the registry would orphan a socket its owner ' +
      'still holds a ref to, leaking the connection instead of closing it.',
  },
  {
    id: 'daemonPushFrameStreamAtom',
    reason:
      'the desktop daemon push-frame stream is OWNED by useWebBridgeClient for exactly the same ' +
      'reason as bridgeEventStreamAtom above: its effect is keyed on isAuthenticated, so it ' +
      'closes the stream and nulls this slot when the phase leaves `usable`.',
  },
  // ── device-local UI preferences that deliberately outlive a sign-out ───────
  ...(
    [
      'sidebarVisibleAtom',
      'sidebarWidthAtom',
      'chatFilesOpenAtom',
      'chatDockOpenAtom',
      'chatDockWidthAtom',
      'tasksPanelCollapsedAtom',
      'tasksShowDoneAtom',
      'tasksPanelLayoutAtom',
      'tasksPanelHeightAtom',
      'shogunFloatingRectAtom',
      'shogunDockedSizeAtom',
      'sessionGroupingAtom',
      'sessionSortAtom',
      'sessionShowEmptyGroupsAtom',
      'sessionCollapsedGroupsAtom',
    ] as const
  ).map((id) => ({
    id,
    reason:
      'device-local layout preference. It describes how THIS window is arranged, carries no ' +
      'account data, and resetting it on every sign-out would be a UX regression, not a fix.',
  })),
];

/** Which barrier is sweeping: the route to `no_credential`, or an in-place re-tenant (sdk ADR-011). */
export type TeardownScope = 'sign-out' | 'identity-change';

/**
 * Reclaim every registered account-scoped store.
 *
 * Called by `clearAuthAction` AFTER the `LocalClear` event has moved the holder
 * to `no_credential`, so no derived projection can briefly report a tenancy for
 * a store that has already been emptied.
 */
export async function resetAccountScopedStores(
  get: Getter,
  set: Setter,
  scope: TeardownScope = 'sign-out',
): Promise<void> {
  // Every entry is invoked BEFORE any is awaited, so a slow cross-process
  // reclaim (the bridge context's IPC push) cannot delay the synchronous ones.
  const pending: Promise<void>[] = [];
  for (const entry of ACCOUNT_SCOPED_TEARDOWN) {
    if (scope === 'identity-change' && entry.identityChangeSkipReason !== undefined) continue;
    const settled = entry.reset(get, set);
    if (settled !== undefined) pending.push(settled);
  }
  await Promise.all(pending);
  const { sdkAtom } = await import('../sdk/atoms');
  get(sdkAtom)?.chat.resetDraft();
}

/**
 * The coverage diff, kept as a pure function so the test that proves the gate
 * WORKS can feed it a fabricated store name without touching the real tree —
 * the same shape as `uncoveredTables` in
 * `go/internal/account/architecture_test.go`.
 */
export function uncoveredStores(
  live: readonly string[],
  registry: readonly AccountScopedStore[] = ACCOUNT_SCOPED_TEARDOWN,
  exemptions: readonly AccountScopedExemption[] = ACCOUNT_SCOPED_EXEMPTIONS
): string[] {
  const covered = new Set<string>([
    ...registry.map((entry) => entry.id),
    ...exemptions.map((exemption) => exemption.id),
  ]);
  return live.filter((id) => !covered.has(id)).sort();
}
